import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Copy, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { EntityCombobox } from "@/components/ui/entity-combobox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useProjects } from "@/hooks/useProjects";
import {
  getNextVersionNumber,
  Quote,
  QuoteVersion,
  useCreateQuote,
  useCreateQuoteVersion,
  useProjectQuotes,
} from "@/hooks/useQuotes";
import { useDuplicateVersionContent } from "@/hooks/useQuoteEntities";
import { formatProjectName } from "@/lib/format-project-name";

type CopyMode = "existing" | "new";

interface CopyQuoteVersionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentProjectId: string;
  sourceQuote: Quote;
  sourceVersion: QuoteVersion | null;
}

function getProjectLabel(
  project: ReturnType<typeof useProjects>["data"][number],
) {
  const name = formatProjectName(project.name, project.event_start_date);
  return project.number ? `${project.number} - ${name}` : name;
}

function getProjectSearchLabel(
  project: ReturnType<typeof useProjects>["data"][number],
) {
  return [
    project.number,
    formatProjectName(project.name, project.event_start_date),
  ]
    .filter(Boolean)
    .join(" ");
}

export function CopyQuoteVersionDialog({
  open,
  onOpenChange,
  currentProjectId,
  sourceQuote,
  sourceVersion,
}: CopyQuoteVersionDialogProps) {
  const navigate = useNavigate();
  const [targetProjectId, setTargetProjectId] = useState(currentProjectId);
  const [copyMode, setCopyMode] = useState<CopyMode>("existing");
  const [targetQuoteId, setTargetQuoteId] = useState("");
  const [newQuoteName, setNewQuoteName] = useState(sourceQuote.name);

  const { data: projects = [] } = useProjects();
  const { data: targetQuotes = [], isLoading: quotesLoading } =
    useProjectQuotes(targetProjectId);
  const createQuote = useCreateQuote();
  const createVersion = useCreateQuoteVersion();
  const duplicateContent = useDuplicateVersionContent();

  const activeProjects = useMemo(
    () =>
      projects
        .filter((project) => !project.deleted_at)
        .sort((a, b) => getProjectLabel(a).localeCompare(getProjectLabel(b))),
    [projects],
  );
  const projectOptions = useMemo(
    () =>
      activeProjects.map((project) => ({
        value: project.id,
        label: getProjectSearchLabel(project),
        subtitle: project.companies?.name ?? undefined,
      })),
    [activeProjects],
  );

  useEffect(() => {
    if (!open) return;
    setTargetProjectId(currentProjectId);
    setCopyMode("existing");
    setTargetQuoteId("");
    setNewQuoteName(sourceQuote.name);
  }, [currentProjectId, open, sourceQuote.name, sourceVersion?.id]);

  useEffect(() => {
    if (copyMode !== "existing") return;
    if (
      targetQuoteId &&
      targetQuotes.some((quote) => quote.id === targetQuoteId)
    ) {
      return;
    }
    setTargetQuoteId(targetQuotes[0]?.id ?? "");
  }, [copyMode, targetQuoteId, targetQuotes]);

  const isPending =
    createQuote.isPending ||
    createVersion.isPending ||
    duplicateContent.isPending;
  const canCopy =
    !!sourceVersion &&
    !!targetProjectId &&
    (copyMode === "new" ? !!newQuoteName.trim() : !!targetQuoteId);

  const createCopiedVersion = async (quote: Quote, versionNumber: number) => {
    if (!sourceVersion) throw new Error("No source version selected");
    const version = await createVersion.mutateAsync({
      quoteId: quote.id,
      versionNumber,
      internalName: sourceVersion.internal_name ?? undefined,
      quoteDate: sourceVersion.quote_date ?? undefined,
      validityDays: sourceVersion.validity_days,
      projectDiscountPercent: sourceVersion.project_discount_percent,
      vatRate: sourceVersion.vat_rate as "CH_8_1" | "NONE",
      generalConditions: sourceVersion.general_conditions ?? undefined,
    });
    await duplicateContent.mutateAsync({
      sourceVersionId: sourceVersion.id,
      targetVersionId: version.id,
    });
    return version;
  };

  const handleCopy = async () => {
    if (!canCopy || !sourceVersion) return;

    let targetQuote: Quote;
    if (copyMode === "new") {
      targetQuote = await createQuote.mutateAsync({
        projectId: targetProjectId,
        name: newQuoteName.trim(),
      });
      const copiedVersion = await createCopiedVersion(targetQuote, 1);
      onOpenChange(false);
      navigate(
        `/projects/${targetProjectId}/finance/quotes/${targetQuote.id}/versions/${copiedVersion.id}`,
      );
      return;
    }

    const existingQuote = targetQuotes.find(
      (quote) => quote.id === targetQuoteId,
    );
    if (!existingQuote) return;
    targetQuote = existingQuote;
    const copiedVersion = await createCopiedVersion(
      targetQuote,
      getNextVersionNumber(targetQuote),
    );
    onOpenChange(false);
    navigate(
      `/projects/${targetProjectId}/finance/quotes/${targetQuote.id}/versions/${copiedVersion.id}`,
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Copy className="h-5 w-5" />
            Copy Quote Version
          </DialogTitle>
          <DialogDescription>
            Copy V{sourceVersion?.version_number ?? ""} from {sourceQuote.name}
            to an existing quote or a new quote.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Project</Label>
            <EntityCombobox
              options={projectOptions}
              value={targetProjectId}
              onChange={(value) => {
                if (value) setTargetProjectId(value);
              }}
              placeholder="Select project"
              searchPlaceholder="Search projects..."
              emptyMessage="No projects found."
              allowClear={false}
            />
          </div>

          <RadioGroup
            value={copyMode}
            onValueChange={(value) => setCopyMode(value as CopyMode)}
            className="grid gap-3 sm:grid-cols-2"
          >
            <Label className="flex cursor-pointer items-center gap-3 rounded-md border p-3">
              <RadioGroupItem value="existing" />
              <span className="text-sm font-medium">Existing quote</span>
            </Label>
            <Label className="flex cursor-pointer items-center gap-3 rounded-md border p-3">
              <RadioGroupItem value="new" />
              <span className="text-sm font-medium">New quote</span>
            </Label>
          </RadioGroup>

          {copyMode === "existing" ? (
            <div className="space-y-2">
              <Label>Quote</Label>
              <Select
                value={targetQuoteId}
                onValueChange={setTargetQuoteId}
                disabled={quotesLoading || targetQuotes.length === 0}
              >
                <SelectTrigger>
                  <SelectValue
                    placeholder={
                      quotesLoading ? "Loading quotes..." : "Select quote"
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {targetQuotes.map((quote) => (
                    <SelectItem key={quote.id} value={quote.id}>
                      {quote.quote_number} - {quote.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {!quotesLoading && targetQuotes.length === 0 ? (
                <p className="text-xs text-muted-foreground">
                  This project has no quotes yet. Create a new quote instead.
                </p>
              ) : null}
            </div>
          ) : (
            <div className="space-y-2">
              <Label htmlFor="copy-quote-new-name">Quote name</Label>
              <Input
                id="copy-quote-new-name"
                value={newQuoteName}
                onChange={(event) => setNewQuoteName(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") void handleCopy();
                }}
              />
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            offlineEdit
            onClick={handleCopy}
            disabled={!canCopy || isPending}
          >
            <FileText className="h-4 w-4 mr-1" />
            Copy
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
